import { translateBackend } from '@assets/i18n/config';
import type { TranslatedString } from '@interfaces/shared';
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Alert } from '@ovComponents/2-component/alert/alert';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import type { WorkflowCustodianDepositTransfer } from '@ovComponents/4-module/workflowCompletion/workflowTypes';
import { formatMoneyValue } from '@utils/moneyFormatting';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  ArchivingAsynchronousMessage,
  ArchivingSynchronousMessage,
} from './components/archiveLoadingIndicators';
import { ArchiveObjectsTable } from './components/archiveObjectsTable';
import { NoObjectsToArchive } from './components/noObjectsToArchive';

type ArchiveObjectsRows = Omit<
  ReturnType<
    typeof import('./hooks/useArchiveObjectsData').useArchiveObjectsData
  >,
  'loading'
>;

export type ArchiveObjectsOptions = {
  title: TranslatedString;
  subtitle: TranslatedString;
  accountLabel: TranslatedString;
  subAccountLabel: TranslatedString;
  goalLabel: TranslatedString;
  transferLabel: TranslatedString;
  scheduledTransferLabel: TranslatedString;
  externalTransferLabel: TranslatedString;
  custodianDepositTransferLabel: TranslatedString;
  showStepInWorkflow: boolean;
  showObjectsToArchive: boolean;
};

export interface ArchiveObjectsVisualProps extends ArchiveObjectsRows {
  readonly options: ArchiveObjectsOptions;
  readonly loading: boolean;
  readonly archiving: boolean;
  readonly onSubmit: () => void;
  readonly presentObjectTypes?: string;
  readonly custodianDepositTransfers: WorkflowCustodianDepositTransfer[];
}

export const ArchiveObjectsVisual: FC<ArchiveObjectsVisualProps> = ({
  options,
  loading,
  archiving,
  onSubmit,
  presentObjectTypes,
  accounts,
  goals,
  subAccounts,
  transfers,
  scheduledTransfers,
  externalTransfers,
  custodianDepositTransfers,
}) => {
  const { t } = useTranslation(['workflowCompletions']);
  const {
    accountLabel,
    subAccountLabel,
    goalLabel,
    transferLabel,
    scheduledTransferLabel,
    externalTransferLabel,
    custodianDepositTransferLabel,
    showObjectsToArchive,
    showStepInWorkflow,
    title,
    subtitle,
  } = options;

  const synchronousArchiving = showStepInWorkflow && archiving;
  const asynchronousArchiving = !showStepInWorkflow && archiving;

  const hasObjectsToArchive = [
    accounts,
    goals,
    subAccounts,
    transfers,
    scheduledTransfers,
    externalTransfers,
    custodianDepositTransfers,
  ].some((objects) => objects.length > 0);

  if (synchronousArchiving) {
    return <ArchivingSynchronousMessage title={title} subtitle={subtitle} />;
  }

  if (asynchronousArchiving) {
    return (
      <ArchivingAsynchronousMessage presentObjectTypes={presentObjectTypes} />
    );
  }

  return (
    <WorkflowCompletionForm
      onSubmit={onSubmit}
      submitButtonProps={{
        label: t('workflowCompletions:continue'),
        disabled: loading,
        dataTestId: 'archive-objects-continue-button',
      }}
    >
      <Typography variant='displayLarge' sx={{ mt: 1 }}>
        {translateBackend(title)}
      </Typography>
      <Typography variant='bodyLarge' sx={{ mb: 3 }}>
        <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
          {translateBackend(subtitle)}
        </ReactMarkdown>
      </Typography>

      {showObjectsToArchive &&
        !loading &&
        (hasObjectsToArchive ? (
          <Box>
            <Alert severity='warning' sx={{ mb: 2 }}>
              <Typography variant='bodyLarge'>
                {t('workflowCompletions:archiveObjects.archiveObjectsWarning')}
              </Typography>
            </Alert>

            {accounts.length > 0 && (
              <ArchiveObjectsTable
                title={translateBackend(accountLabel)}
                columns={[
                  t('workflowCompletions:archiveObjects.type'),
                  t('workflowCompletions:archiveObjects.state'),
                ]}
                rows={accounts.map((account) => [
                  translateBackend(account.translatedAccountType) || '',
                  translateBackend(account.stateTranslated) || '',
                ])}
              />
            )}

            {subAccounts.length > 0 && (
              <ArchiveObjectsTable
                title={translateBackend(subAccountLabel)}
                columns={[
                  t('workflowCompletions:archiveObjects.goalName'),
                  t('workflowCompletions:archiveObjects.accountType'),
                ]}
                rows={subAccounts.map((sa) => [
                  sa.goal?.name ?? '',
                  translateBackend(sa.account.translatedAccountType) || '',
                ])}
              />
            )}

            {goals.length > 0 && (
              <ArchiveObjectsTable
                title={translateBackend(goalLabel)}
                columns={[t('workflowCompletions:archiveObjects.name')]}
                rows={goals.map((goal) => [goal.name ?? ''])}
              />
            )}

            {transfers.length > 0 && (
              <ArchiveObjectsTable
                title={translateBackend(transferLabel)}
                columns={[
                  t('workflowCompletions:archiveObjects.type'),
                  t('workflowCompletions:archiveObjects.amount'),
                  t('workflowCompletions:archiveObjects.currency'),
                ]}
                rows={transfers.map((tr) => [
                  translateBackend(tr.typeTranslated) ?? '',
                  formatMoneyValue(tr.amountCents),
                  tr.currency ?? '',
                ])}
              />
            )}

            {scheduledTransfers.length > 0 && (
              <ArchiveObjectsTable
                title={translateBackend(scheduledTransferLabel)}
                columns={[
                  t('workflowCompletions:archiveObjects.type'),
                  t('workflowCompletions:archiveObjects.amount'),
                  t('workflowCompletions:archiveObjects.currency'),
                ]}
                rows={scheduledTransfers.map((st) => [
                  translateBackend(st.typeTranslated) ?? '',
                  formatMoneyValue(st.amountCents ?? 0),
                  st.currency ?? '',
                ])}
              />
            )}

            {externalTransfers.length > 0 && (
              <ArchiveObjectsTable
                title={translateBackend(externalTransferLabel)}
                columns={[
                  t('workflowCompletions:archiveObjects.type'),
                  t('workflowCompletions:archiveObjects.institution'),
                  t('workflowCompletions:archiveObjects.transferMethod'),
                ]}
                rows={externalTransfers.map((et) => [
                  et.type,
                  et.institution?.name ?? '',
                  et.transferMethod != null ? String(et.transferMethod) : '',
                ])}
              />
            )}

            {custodianDepositTransfers.length > 0 && (
              <ArchiveObjectsTable
                title={translateBackend(custodianDepositTransferLabel)}
                columns={[
                  t('workflowCompletions:archiveObjects.accountId'),
                  t('workflowCompletions:archiveObjects.amount'),
                  t('workflowCompletions:archiveObjects.frequency'),
                  t('workflowCompletions:archiveObjects.startDate'),
                ]}
                rows={custodianDepositTransfers.map(
                  (custodianDepositTransfer) => [
                    String(custodianDepositTransfer.accountId ?? ''),
                    String(custodianDepositTransfer.amount ?? ''),
                    String(custodianDepositTransfer.frequency ?? ''),
                    String(custodianDepositTransfer.startDate ?? ''),
                  ],
                )}
              />
            )}
          </Box>
        ) : (
          <NoObjectsToArchive />
        ))}
    </WorkflowCompletionForm>
  );
};
